import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { accounts as accountRes, customers as customerRes } from "@/app/master";
import { Pager } from "@/app/Pager";
import { compareNullable } from "@/app/sort";
import { ClearFilters } from "@/app/ClearFilters";
import { accountOptions, loadingOf, useList, useNames } from "@/app/queries";
import { ShowPicker } from "@/app/ShowPicker";
import { ToolIcon } from "@/app/toolIcons";
import { useShown } from "@/app/useShown";
import { useSearchFilters } from "@/app/useSearchFilters";
import { changeOf, countryName, formatCount, formatRate, rateDiff, rateOf, traffic, type TrafficCount, type TrafficRow } from "@/app/traffic";
import { usePage } from "@/app/usePage";
import { Alert, Button, Combobox, cx, Delta, EmptyState, FILTER_W, formatTime, PageHeader, Spinner, Table, TableWrap, Th, Toolbar, ToolbarCount, useToast } from "@/ui";

/** 一个可排序的数字列：value 取排序用的数，cell 画单元格。 */
interface Column { key: string; label: string; value: (r: TrafficRow) => number | null; cell: (r: TrafficRow) => ReactNode }

/** 一组列：今日、昨日同时段、较昨日同时段、昨日全天；short 是“显示列”里列名前的组名。 */
interface Group { title: string; short: string; columns: Column[] }

/** 一个时段的提交、送达与送达率三列（withRate 为假时只有前两列）。 */
function countColumns(key: string, pick: (r: TrafficRow) => TrafficCount, withRate = true): Column[] {
  const cols: Column[] = [
    { key: `${key}.submitted`, label: "提交", value: (r) => pick(r).submitted, cell: (r) => formatCount(pick(r).submitted) },
    { key: `${key}.delivered`, label: "送达", value: (r) => pick(r).delivered, cell: (r) => formatCount(pick(r).delivered) },
  ];
  if (withRate) cols.push({ key: `${key}.rate`, label: "送达率", value: (r) => rateOf(pick(r)), cell: (r) => formatRate(rateOf(pick(r))) });
  return cols;
}

/** 变化量单元格：相对变化写百分比，送达率之差写百分点，保留一位；舍入后为 0 的不带箭头，没有可比的基数时是"—"。 */
function delta(v: number | null, unit: "%" | "pp") {
  if (v === null) return <Delta direction={0} />;
  const shown = Math.round((unit === "%" ? v * 100 : v) * 10) / 10;
  return <Delta direction={Math.sign(shown)} text={`${Math.abs(shown).toFixed(1)}${unit}`} />;
}

const change = (field: "submitted" | "delivered") => (r: TrafficRow) => changeOf(r.today[field], r.same_period[field]);

const GROUPS: Group[] = [
  { title: "今日（截至现在）", short: "今日", columns: countColumns("today", (r) => r.today) },
  { title: "昨日同时段", short: "昨日同时段", columns: countColumns("same", (r) => r.same_period) },
  {
    title: "较昨日同时段",
    short: "较昨日",
    columns: [
      { key: "change.submitted", label: "提交", value: change("submitted"), cell: (r) => delta(change("submitted")(r), "%") },
      { key: "change.delivered", label: "送达", value: change("delivered"), cell: (r) => delta(change("delivered")(r), "%") },
      { key: "change.rate", label: "送达率", value: (r) => rateDiff(r.today, r.same_period), cell: (r) => delta(rateDiff(r.today, r.same_period), "pp") },
    ],
  },
  { title: "昨日全天", short: "昨日全天", columns: countColumns("yesterday", (r) => r.yesterday, false) },
];
const COLUMNS = GROUPS.flatMap((g) => g.columns);
const COLUMN_KEYS = COLUMNS.map((c) => c.key);
/** “显示列”的选项：指标列可逐列隐藏，客户与 MCC 两个维度列固定（#541）。 */
const COLUMN_OPTIONS = GROUPS.flatMap((g) => g.columns.map((c) => ({ key: c.key, label: `${g.short} ${c.label}` })));
/** 维度列宽：客户名后带账号，MCC 后带国家名；指标列一律数值宽度，最后一列吸收剩余（#541，决策 D199）。 */
const DIM_WIDTHS = [240, 156] as const;

/** 按列排序，没有值的总在最后。 */
function sortRows(rows: TrafficRow[], key: string, desc: boolean): TrafficRow[] {
  const col = COLUMNS.find((c) => c.key === key) ?? COLUMNS[0];
  return [...rows].sort((a, b) => compareNullable(col.value(a), col.value(b), desc));
}

/** 流量监控：按 客户 × MCC 看今天到现在与昨天同一时段的提交、送达与送达率（北京时间，决策 D98），可按账号、MCC 筛选，点行看这个客户在这个 MCC 上的曲线。
 * 每 10 秒自动刷新（admin 端另有 10 秒缓存）；手动刷新跳过缓存。筛选条件在地址栏里，从详情返回时还在。 */
export function Traffic() {
  const navigate = useNavigate();
  const location = useLocation();
  const names = useNames();
  const accountList = useList("accounts", accountRes);
  const customerList = useList("customers", customerRes);
  const { filters, set, clear, active } = useSearchFilters(["account", "mcc"]);
  const [sort, setSort] = useState({ key: "today.submitted", desc: true });
  const [columns, setColumns] = useShown("columns", "traffic", COLUMN_KEYS, COLUMN_KEYS);
  // 只留显示的列；一组都隐藏了，分组表头也不画
  const groups = GROUPS.map((g) => ({ ...g, columns: g.columns.filter((c) => columns.includes(c.key)) })).filter((g) => g.columns.length);
  const widths = [...DIM_WIDTHS, ...groups.flatMap((g) => g.columns.map(() => "num" as const))];
  const key = ["traffic", filters.account];
  const q = useQuery({ queryKey: key, queryFn: () => traffic.list(filters.account), refetchInterval: 10_000 });
  const client = useQueryClient();
  const toast = useToast();
  // 手动刷新失败用 Toast 报一次，不留常驻提示：之后的自动刷新成功了，页面上不该还挂着"加载失败"
  const refresh = useMutation({ mutationFn: () => traffic.list(filters.account, true), onSuccess: (d) => client.setQueryData(key, d), onError: (e) => toast(`刷新失败：${e.message}`, { tone: "danger" }) });
  const all = q.data?.rows ?? [];
  const mccs = [...new Map(all.map((r) => [r.mcc, r.country])).entries()].sort(([a], [b]) => a.localeCompare(b));
  const rows = sortRows(all.filter((r) => !filters.mcc || r.mcc === filters.mcc), sort.key, sort.desc);
  const paged = usePage("traffic", rows, filters);
  // 带着账号筛选进详情；返回时回到带着原筛选条件的列表
  const open = (r: TrafficRow) => navigate(`/traffic/${r.customer_id}/${r.mcc}${filters.account ? `?account=${filters.account}` : ""}`, { state: { back: location.search } });
  const accounts = (r: TrafficRow) => (r.account_ids.length === 1 ? `${names.account(r.account_ids[0])} #${r.account_ids[0]}` : `${r.account_ids.length} 个账号`);
  const onSort = (key: string) => setSort((s) => ({ key, desc: s.key === key ? !s.desc : true }));

  return (
    <>
      <PageHeader title="流量监控" description="按消息接收时刻归入时段（北京时间），回执晚到记回原时段；昨日同时段是昨天零点到此刻减一天。点一行看曲线与分通道、分账号。" />
      {q.error && <Alert tone="danger" title="加载失败">{q.error.message}</Alert>}
      <Toolbar actions={<>
        <ShowPicker label="显示列" icon={<ToolIcon name="columns-3" />} heading="显示的列" options={COLUMN_OPTIONS} shown={columns} onChange={setColumns} />
        {active && <ClearFilters onClear={clear} />}
      </>} right={<>{q.data && <ToolbarCount>更新于 {formatTime(q.data.now)}</ToolbarCount>}{(q.isFetching || refresh.isPending) && <Spinner label="刷新中" />}<Button size="sm" disabled={refresh.isPending} onClick={() => refresh.mutate()}>刷新</Button></>}>
        <Combobox size="sm" width={FILTER_W.select} ariaLabel="账号" searchPlaceholder="搜索账号名称或 system_id" options={[{ value: "", label: "全部账号" }, ...accountOptions(accountList.data, customerList.data)]} value={filters.account} onChange={(v) => set("account", v)} />
        <Combobox size="sm" width={FILTER_W.select} ariaLabel="MCC" searchPlaceholder="搜索 MCC 或国家" options={[{ value: "", label: "全部 MCC" }, ...mccs.map(([m, c]) => ({ value: m, label: `${m} ${countryName(c)}` }))]} value={filters.mcc} onChange={(v) => set("mcc", v)} />
      </Toolbar>
      <TableWrap loading={loadingOf(q)}>
        {rows.length ? (
          <>
            <Table cols={widths.map((w, i) => (i === widths.length - 1 ? undefined : w))}>
              <thead>
                <tr>
                  <Th colSpan={2} className="ax-th-group">维度</Th>
                  {groups.map((g) => <Th key={g.title} colSpan={g.columns.length} className="ax-th-group is-group-start">{g.title}</Th>)}
                </tr>
                <tr>
                  <Th>客户</Th><Th>MCC</Th>
                  {groups.flatMap((g) => g.columns.map((c, i) => (
                    <Th key={c.key} numeric sortable sorted={sort.key === c.key && (sort.desc ? "desc" : "asc")} className={cx(i === 0 && "is-group-start")} onClick={() => onSort(c.key)}>{c.label}</Th>
                  )))}
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((r) => (
                  // 客户 0 是不在主数据里的账号（已删除的与系统账号），详情按客户取账号，点进去没有数据，所以不可点
                  <tr key={`${r.customer_id}:${r.mcc}`} {...(r.customer_id ? { className: "is-clickable", tabIndex: 0, onClick: () => open(r), onKeyDown: (e: KeyboardEvent) => e.key === "Enter" && open(r) } : {})}>
                    <td>{r.customer_id ? names.customer(r.customer_id) : "已删除的账号"} <span className="ax-secondary">{accounts(r)}</span></td>
                    <td className="ax-mono">{r.mcc} <span className="ax-secondary">{countryName(r.country)}</span></td>
                    {groups.flatMap((g) => g.columns.map((c, i) => <td key={c.key} className={cx("ax-num", i === 0 && "is-group-start")}>{c.cell(r)}</td>))}
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pager {...paged} />
          </>
        ) : (
          !q.isPending && <EmptyState title={active ? "没有符合筛选条件的流量" : "今天和昨天都还没有流量"}>{active ? "" : "客户提交的消息十几秒内会出现在这里。一直没有数据时，请联系运维检查流量统计服务是否在运行。"}</EmptyState>
        )}
      </TableWrap>
    </>
  );
}
